import React, { createContext, useContext, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { isAdminTokenValid, adminLogin, clearAdminToken } from './adminApi';
import './components/admin/admin.css';
import AdminOverview from './components/admin/AdminOverview';
import AdminCompanies from './components/admin/AdminCompanies';
import AdminTokenUsage from './components/admin/AdminTokenUsage';
import AdminFeedback from './components/admin/AdminFeedback';
import AdminConversations from './components/admin/AdminConversations';
import AdminTraces from './components/admin/AdminTraces';
import AdminHelp from './components/admin/AdminHelp';

export const AdminCompanyContext = createContext({
  selectedCompany: null,
  setSelectedCompany: () => {},
  companyLabel: null,
  setCompanyLabel: () => {},
});

// ─── Login modal ─────────────────────────────────────────────────────────────

function AdminLoginModal({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminLogin(username, password);
      onLogin();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login-overlay">
      <form className="admin-login-modal" onSubmit={handleSubmit}>
        <div className="admin-login-header">
          <span className="admin-badge">ADMIN</span>
          <h2>LabV Admin</h2>
        </div>
        {error && <div className="admin-login-error">{error}</div>}
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          autoFocus
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { to: '/admin', label: 'Overview', end: true },
  { to: '/admin/companies', label: 'Companies' },
  { to: '/admin/token-usage', label: 'Token Usage' },
  { to: '/admin/feedback', label: 'Feedback' },
  { to: '/admin/conversations', label: 'Conversations' },
  { to: '/admin/traces', label: 'Traces' },
  { to: '/admin/help', label: 'Help' },
];

function AdminSidebar({ onLogout }) {
  const { selectedCompany, setSelectedCompany, companyLabel, setCompanyLabel } = useContext(AdminCompanyContext);

  return (
    <aside className="admin-sidebar">
      <div className="admin-sidebar-header">
        <span className="admin-badge">ADMIN</span>
        <span className="admin-sidebar-title">LabV</span>
      </div>

      {selectedCompany && (
        <div className="admin-company-filter">
          <span title={selectedCompany}>
            {companyLabel || (selectedCompany.length > 14 ? selectedCompany.slice(0, 8) + '…' : selectedCompany)}
          </span>
          <button onClick={() => { setSelectedCompany(null); setCompanyLabel(null); }} title="Clear company filter">✕</button>
        </div>
      )}

      <nav className="admin-nav">
        {NAV_ITEMS.map(({ to, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <button className="admin-logout-btn" onClick={onLogout}>
        Logout
      </button>
    </aside>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────

export default function AdminApp() {
  const [authed, setAuthed] = useState(isAdminTokenValid());
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyLabel, setCompanyLabel] = useState(null);

  function handleLogout() {
    clearAdminToken();
    setAuthed(false);
    setSelectedCompany(null);
    setCompanyLabel(null);
  }

  if (!authed) {
    return <AdminLoginModal onLogin={() => setAuthed(true)} />;
  }

  return (
    <AdminCompanyContext.Provider value={{ selectedCompany, setSelectedCompany, companyLabel, setCompanyLabel }}>
      <div className="admin-layout">
        <AdminSidebar onLogout={handleLogout} />
        <main className="admin-content">
          <Routes>
            <Route path="/admin" element={<AdminOverview />} />
            <Route path="/admin/companies" element={<AdminCompanies />} />
            <Route path="/admin/token-usage" element={<AdminTokenUsage />} />
            <Route path="/admin/feedback" element={<AdminFeedback />} />
            <Route path="/admin/conversations" element={<AdminConversations />} />
            <Route path="/admin/traces" element={<AdminTraces />} />
            <Route path="/admin/help" element={<AdminHelp />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </AdminCompanyContext.Provider>
  );
}
